"use client";

import { useId, useRef, useState, type FormEvent } from "react";
import { whatsappUrl } from "@/lib/site";
import { IconWhatsApp } from "./Icons";

type Fields = {
  nama: string;
  telepon: string;
  email: string;
  layanan: string;
  lokasi: string;
  pesan: string;
};
type Errors = Partial<Record<keyof Fields, string>>;

type Status =
  | { kind: "idle" }
  | { kind: "sending" }
  | { kind: "sent" }
  | { kind: "error"; message: string }
  | { kind: "no-endpoint"; waLink: string };

/** Diisi saat build melalui variabel lingkungan; kosong = belum ada backend. */
const ENDPOINT = process.env.NEXT_PUBLIC_FORM_ENDPOINT || "";

const EMPTY: Fields = { nama: "", telepon: "", email: "", layanan: "", lokasi: "", pesan: "" };

function validate(v: Fields): Errors {
  const e: Errors = {};
  if (v.nama.trim().length < 2) e.nama = "Mohon isi nama Anda (minimal 2 karakter).";
  const digits = v.telepon.replace(/[\s\-().+]/g, "");
  if (!digits) e.telepon = "Mohon isi nomor telepon atau WhatsApp.";
  else if (!/^\d{9,15}$/.test(digits)) e.telepon = "Nomor tidak valid. Gunakan 9–15 digit angka, mis. 0812 3456 7890.";
  if (v.email.trim() && !/^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/.test(v.email.trim()))
    e.email = "Format email tidak valid, mis. nama@domain.com.";
  if (!v.layanan) e.layanan = "Mohon pilih layanan yang Anda butuhkan.";
  if (v.pesan.trim().length < 10) e.pesan = "Mohon jelaskan kebutuhan Anda (minimal 10 karakter).";
  return e;
}

function toWhatsAppText(v: Fields): string {
  return [
    "Halo Ranutama Teknik Indonesia, saya ingin meminta informasi.",
    `Nama: ${v.nama.trim()}`,
    `Telepon/WA: ${v.telepon.trim()}`,
    v.email.trim() ? `Email: ${v.email.trim()}` : "",
    `Layanan: ${v.layanan}`,
    v.lokasi.trim() ? `Lokasi proyek: ${v.lokasi.trim()}` : "",
    `Kebutuhan: ${v.pesan.trim()}`,
  ]
    .filter(Boolean)
    .join("\n");
}

export function ContactForm({ services }: { services: string[] }) {
  const uid = useId();
  const fid = (k: keyof Fields) => `${uid}-${k}`;
  const [values, setValues] = useState<Fields>(EMPTY);
  const [errors, setErrors] = useState<Errors>({});
  const [status, setStatus] = useState<Status>({ kind: "idle" });
  const formRef = useRef<HTMLFormElement>(null);
  const statusRef = useRef<HTMLDivElement>(null);

  const update = (k: keyof Fields) => (e: { target: { value: string } }) => {
    const next = { ...values, [k]: e.target.value };
    setValues(next);
    if (errors[k]) setErrors((prev) => ({ ...prev, [k]: validate(next)[k] }));
  };

  async function onSubmit(e: FormEvent<HTMLFormElement>) {
    e.preventDefault();
    const errs = validate(values);
    setErrors(errs);
    const firstInvalid = (Object.keys(errs) as (keyof Fields)[])[0];
    if (firstInvalid) {
      setStatus({ kind: "idle" });
      document.getElementById(fid(firstInvalid))?.focus();
      return;
    }

    if (!ENDPOINT) {
      // Tidak ada backend: jangan tampilkan seolah terkirim.
      setStatus({ kind: "no-endpoint", waLink: whatsappUrl(toWhatsAppText(values)) });
      requestAnimationFrame(() => statusRef.current?.focus());
      return;
    }

    setStatus({ kind: "sending" });
    try {
      const res = await fetch(ENDPOINT, {
        method: "POST",
        headers: { "Content-Type": "application/json", Accept: "application/json" },
        body: JSON.stringify(values),
      });
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      setStatus({ kind: "sent" });
      setValues(EMPTY);
      formRef.current?.reset();
    } catch {
      setStatus({
        kind: "error",
        message: "Maaf, pesan gagal dikirim. Silakan coba lagi atau hubungi kami langsung melalui WhatsApp/telepon.",
      });
    }
    requestAnimationFrame(() => statusRef.current?.focus());
  }

  const errorCount = Object.values(errors).filter(Boolean).length;
  const describedBy = (k: keyof Fields, hint?: boolean) =>
    [hint ? `${fid(k)}-hint` : "", errors[k] ? `${fid(k)}-err` : ""].filter(Boolean).join(" ") || undefined;

  return (
    <form ref={formRef} className="contact-form" noValidate onSubmit={onSubmit} aria-describedby={`${uid}-req`}>
      <p id={`${uid}-req`} className="form-note">
        Kolom bertanda * wajib diisi.
      </p>

      {errorCount > 0 && (
        <div className="form-alert form-alert-error" role="alert">
          Ada {errorCount} isian yang perlu diperbaiki sebelum melanjutkan.
        </div>
      )}

      <div className="form-grid">
        <div className="field">
          <label htmlFor={fid("nama")}>
            Nama lengkap <span aria-hidden="true">*</span>
          </label>
          <input
            id={fid("nama")}
            name="nama"
            type="text"
            autoComplete="name"
            required
            value={values.nama}
            onChange={update("nama")}
            aria-invalid={errors.nama ? true : undefined}
            aria-describedby={describedBy("nama")}
          />
          {errors.nama && (
            <p id={`${fid("nama")}-err`} className="field-error">
              {errors.nama}
            </p>
          )}
        </div>

        <div className="field">
          <label htmlFor={fid("telepon")}>
            Nomor telepon / WhatsApp <span aria-hidden="true">*</span>
          </label>
          <input
            id={fid("telepon")}
            name="telepon"
            type="tel"
            inputMode="tel"
            autoComplete="tel"
            required
            value={values.telepon}
            onChange={update("telepon")}
            aria-invalid={errors.telepon ? true : undefined}
            aria-describedby={describedBy("telepon")}
          />
          {errors.telepon && (
            <p id={`${fid("telepon")}-err`} className="field-error">
              {errors.telepon}
            </p>
          )}
        </div>

        <div className="field">
          <label htmlFor={fid("email")}>Email (opsional)</label>
          <input
            id={fid("email")}
            name="email"
            type="email"
            autoComplete="email"
            value={values.email}
            onChange={update("email")}
            aria-invalid={errors.email ? true : undefined}
            aria-describedby={describedBy("email")}
          />
          {errors.email && (
            <p id={`${fid("email")}-err`} className="field-error">
              {errors.email}
            </p>
          )}
        </div>

        <div className="field">
          <label htmlFor={fid("layanan")}>
            Layanan yang dibutuhkan <span aria-hidden="true">*</span>
          </label>
          <select
            id={fid("layanan")}
            name="layanan"
            required
            value={values.layanan}
            onChange={update("layanan")}
            aria-invalid={errors.layanan ? true : undefined}
            aria-describedby={describedBy("layanan")}
          >
            <option value="">— Pilih layanan —</option>
            {services.map((s) => (
              <option key={s} value={s}>
                {s}
              </option>
            ))}
            <option value="Belum tahu / ingin konsultasi">Belum tahu / ingin konsultasi</option>
          </select>
          {errors.layanan && (
            <p id={`${fid("layanan")}-err`} className="field-error">
              {errors.layanan}
            </p>
          )}
        </div>

        <div className="field field-full">
          <label htmlFor={fid("lokasi")}>Lokasi proyek (opsional)</label>
          <input
            id={fid("lokasi")}
            name="lokasi"
            type="text"
            autoComplete="address-level2"
            value={values.lokasi}
            onChange={update("lokasi")}
            aria-describedby={`${fid("lokasi")}-hint`}
          />
          <p id={`${fid("lokasi")}-hint`} className="field-hint">
            Contoh: Pasar Minggu, Jakarta Selatan.
          </p>
        </div>

        <div className="field field-full">
          <label htmlFor={fid("pesan")}>
            Kebutuhan Anda <span aria-hidden="true">*</span>
          </label>
          <textarea
            id={fid("pesan")}
            name="pesan"
            rows={5}
            required
            value={values.pesan}
            onChange={update("pesan")}
            aria-invalid={errors.pesan ? true : undefined}
            aria-describedby={describedBy("pesan", true)}
          />
          <p id={`${fid("pesan")}-hint`} className="field-hint">
            Ceritakan perkiraan ukuran area, fungsi, dan rencana waktu pengerjaan.
          </p>
          {errors.pesan && (
            <p id={`${fid("pesan")}-err`} className="field-error">
              {errors.pesan}
            </p>
          )}
        </div>
      </div>

      <button type="submit" className="btn btn-primary btn-lg" disabled={status.kind === "sending"}>
        {status.kind === "sending" ? "Mengirim…" : "Kirim permintaan informasi"}
      </button>

      <div ref={statusRef} tabIndex={-1} className="form-status" aria-live="polite">
        {status.kind === "sent" && (
          <div className="form-alert form-alert-success">
            Terima kasih, permintaan Anda sudah kami terima. Tim kami akan menghubungi Anda melalui kontak yang Anda
            berikan.
          </div>
        )}
        {status.kind === "error" && <div className="form-alert form-alert-error">{status.message}</div>}
        {status.kind === "no-endpoint" && (
          <div className="form-alert form-alert-info">
            <p>
              <strong>Data Anda belum terkirim.</strong> Formulir ini belum terhubung ke server penerima pesan. Isian
              Anda sudah lengkap — silakan kirimkan melalui WhatsApp dengan pesan yang sudah kami siapkan:
            </p>
            <a className="btn btn-whatsapp" href={status.waLink} target="_blank" rel="noopener noreferrer">
              <IconWhatsApp size={18} />
              Kirim lewat WhatsApp
            </a>
          </div>
        )}
      </div>
    </form>
  );
}
